import { motion } from "framer-motion";
import { FileText, Shield, AlertTriangle, Scale, Eye, RefreshCw, Mail, ChevronRight } from "lucide-react";
import heroBg from "../assets/hero-bg.jpg";
import Section from "../components/Section";
import PageHero from "../components/PageHero";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const sections = [
  {
    id: "terms",
    icon: FileText,
    title: "Terms & Conditions",
    content: [
      { heading: "1. Platform Services", text: "AeroVolt Energy Ltd provides a renewable energy infrastructure participation platform. The platform connects partners with operational wind and solar energy projects for performance-based revenue sharing. AeroVolt does not offer financial advice, investment management, or guaranteed returns." },
      { heading: "2. Eligibility", text: "Partners must be at least 18 years of age and pass KYC verification. AeroVolt reserves the right to refuse service to individuals or entities that do not meet compliance requirements." },
      { heading: "3. Energy Packages", text: "All energy participation plans have fixed durations and performance-based distribution rates. Distributions depend on actual energy production revenue and are not guaranteed. Capital is locked for the duration of the selected plan." },
      { heading: "4. Referral Program", text: "Referral commissions are earned on the initial package activation of referred partners across up to 5 levels. Commission rates are fixed per level and may be modified with 30 days' written notice." },
      { heading: "5. Binary Bonus", text: "Binary matching bonuses are calculated on the weaker leg's new volume per cycle. There may be daily or weekly caps on binary earnings depending on your plan tier." },
      { heading: "6. Withdrawals", text: "Withdrawal requests are processed weekly on designated payout days. Processing fees apply as displayed at the time of withdrawal. Minimum withdrawal amounts may apply." },
      { heading: "7. Account Termination", text: "AeroVolt reserves the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, or fail to maintain compliance with KYC/AML requirements." },
      { heading: "8. Modifications", text: "These terms may be updated periodically. Partners will be notified of material changes via email and dashboard notification at least 14 days prior to changes taking effect." },
    ],
  },
  {
    id: "privacy",
    icon: Eye,
    title: "Privacy Policy",
    content: [
      { heading: "1. Data Collection", text: "We collect personal information necessary for KYC verification (name, address, ID documents), account management (email, phone), and financial transactions (wallet addresses, payment details). Usage data including IP addresses, browser type, and interaction logs are collected for security and service improvement." },
      { heading: "2. Data Use", text: "Your data is used to verify identity, process transactions, communicate account updates, detect fraud, comply with legal obligations, and improve platform services. We do not sell personal data to third parties." },
      { heading: "3. Data Storage & Security", text: "Personal data is stored on encrypted servers within the European Economic Area (EEA). We employ 256-bit SSL encryption, access controls, and regular security audits to protect your information." },
      { heading: "4. Your Rights (GDPR)", text: "You have the right to access, rectify, erase, restrict processing, port your data, and object to processing. To exercise these rights, contact our Data Protection Officer at privacy@aerovolt.energy." },
      { heading: "5. Cookies", text: "We use essential cookies for platform functionality and analytics cookies to understand usage patterns. You can manage cookie preferences through your browser settings." },
      { heading: "6. Third Parties", text: "We share data with KYC verification providers, payment processors, and cloud infrastructure providers — all bound by data processing agreements compliant with GDPR." },
      { heading: "7. Retention", text: "Personal data is retained for the duration of your account plus 7 years for financial record-keeping requirements. KYC documents are retained as required by anti-money laundering regulations." },
    ],
  },
  {
    id: "risk",
    icon: AlertTriangle,
    title: "Risk Disclosure",
    content: [
      { heading: "1. Performance Risk", text: "All distributions are performance-based, calculated from actual energy production revenue. Energy output varies based on weather conditions, equipment performance, grid capacity, and other operational factors. There is no guarantee of any specific return rate." },
      { heading: "2. Market Risk", text: "Energy prices fluctuate based on market supply and demand, government policy changes, and macroeconomic conditions. Changes in feed-in tariffs or Power Purchase Agreement rates may impact revenue." },
      { heading: "3. Operational Risk", text: "Wind turbines and solar installations are subject to mechanical failure, natural disasters, vandalism, and supply chain disruptions. While insured, such events may temporarily reduce energy output and distributions." },
      { heading: "4. Regulatory Risk", text: "Changes in renewable energy regulations, tax incentives, or government policy in the UK or operating jurisdictions could impact project economics and partner distributions." },
      { heading: "5. Capital Risk", text: "Participation capital is committed for the full term of the selected plan. Early withdrawal is not available. Partners should only participate with funds they can afford to commit for the plan duration." },
      { heading: "6. Network Risk", text: "Referral and binary bonuses depend on the activity and participation of your network. There is no guarantee that team members will maintain their activity or that your network will grow." },
      { heading: "7. No Guaranteed Income", text: "AeroVolt does not guarantee any income, return, or profit from participation. Past performance is not indicative of future results. All performance figures shown are illustrative and historical only." },
      { heading: "8. Independent Advice", text: "AeroVolt strongly recommends seeking independent financial, legal, and tax advice before making any participation decision. Nothing on this platform constitutes financial advice." },
    ],
  },
  {
    id: "refund",
    icon: RefreshCw,
    title: "Refund & Compliance",
    content: [
      { heading: "1. Cooling-Off Period", text: "New partners have a 14-day cooling-off period from the date of first package activation. During this period, you may request a full refund of your participation amount, minus any distributions already received." },
      { heading: "2. After Cooling-Off", text: "Once the 14-day cooling-off period has elapsed, participation capital is committed for the full plan duration and is not eligible for refund." },
      { heading: "3. Platform Model", text: "AeroVolt operates as a renewable energy infrastructure participation platform. We are not a bank, investment fund, or financial institution. We do not accept deposits or provide financial services regulated under FCA rules." },
      { heading: "4. Regulatory Compliance", text: "AeroVolt Energy Ltd is registered in England and Wales. Financial promotions are subject to FCA oversight. We maintain compliance with UK anti-money laundering regulations and data protection laws." },
      { heading: "5. Complaints", text: "Complaints should be directed to complaints@aerovolt.energy. We aim to acknowledge complaints within 2 business days and provide a final response within 8 weeks." },
    ],
  },
];

const Terms = () => (
  <div>
    <PageHero
      eyebrow="Legal"
      title={<>Terms, Privacy & <span className="energy-gradient-text">Risk Disclosure</span></>}
      description="Complete transparency in every policy. Read our terms of service, privacy policy, risk disclosure, and refund policy carefully before participating."
      backgroundImage={heroBg}
    />

    {/* Quick Nav */}
    <Section>
      <div className="flex flex-wrap justify-center gap-3 mb-12">
        {sections.map((s, i) => (
          <motion.a
            key={i}
            href={`#${s.id}`}
            {...fadeUp}
            transition={{ delay: i * 0.1 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl glass-card hover:border-primary/30 transition-all text-sm font-medium"
          >
            <s.icon className="w-4 h-4 text-primary" />
            {s.title}
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
          </motion.a>
        ))}
      </div>

      {/* Content Sections */}
      <div className="max-w-4xl mx-auto space-y-16">
        {sections.map((section, si) => (
          <motion.div key={si} id={section.id} {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl energy-gradient flex items-center justify-center">
                <section.icon className="w-5 h-5 text-primary-foreground" />
              </div>
              <h2 className="font-display text-2xl font-bold">{section.title}</h2>
            </div>
            <div className="space-y-6">
              {section.content.map((item, i) => (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }} className="p-5 rounded-xl glass-card">
                  <h3 className="font-display text-sm font-semibold mb-2">{item.heading}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Contact for questions */}
    <Section className="bg-secondary/20">
      <motion.div {...fadeUp} className="text-center max-w-xl mx-auto">
        <Mail className="w-10 h-10 text-primary mx-auto mb-4" />
        <h3 className="font-display text-xl font-bold mb-3">Questions About Our Policies?</h3>
        <p className="text-sm text-muted-foreground mb-4">Our legal and compliance team is available to address any questions about our terms, privacy practices, or risk disclosures.</p>
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">Legal: <span className="text-primary">legal@aerovolt.energy</span></p>
          <p className="text-muted-foreground">Privacy: <span className="text-primary">privacy@aerovolt.energy</span></p>
          <p className="text-muted-foreground">Complaints: <span className="text-primary">complaints@aerovolt.energy</span></p>
        </div>
      </motion.div>
    </Section>

    {/* Last updated */}
    <div className="text-center py-6 text-xs text-muted-foreground border-t border-border">
      Last updated: February 2026 | AeroVolt Energy Ltd, Canary Wharf, London, UK
    </div>
  </div>
);

export default Terms;
